import api from 'config/axiosConfig';

// Types for API responses
interface ApiResponse<T = any> {
    data: T;
    message?: string;
}

// Privacy levels for user data
export type PrivacyLevel = 'public' | 'follow' | 'members' | 'teams' | 'besteas' | 'me' | 'admin';

// Types for different setting categories
export interface PrivacySettings {
    is_private?: boolean;
    content_default_audience?: PrivacyLevel;
    message_receive?: ('public' | 'teams' | 'follow')[];
}

export interface PreferenceSettings {
    auth_otp_login?: boolean;
    // Add other preference settings as needed
}

export interface AboutValue<T = any> {
    value: T;
    privacy: PrivacyLevel;
}

export interface AboutSettings {
    bio_brief: AboutValue<string>;
    bio_links: AboutValue<Array<{ type: 'custom' | 'instagram' | 'x' | 'facebook' | 'youtube' | 'tiktok' | 'reddit', label: string; url: string }>>;
    top_issue: AboutValue<number | null>;
    alignment: AboutValue<Record<string, string>>;
    birthdate: AboutValue<string>;
    age: AboutValue<number>;
    education: AboutValue<string>;
    location: AboutValue<string>;
    religion: AboutValue<string[]>;
    political_identities: AboutValue<string[]>;
    political_parties: AboutValue<string[]>;
    gender: AboutValue<string>;
}

// Helper function for getting user by id
export async function getUserById(userId: number): Promise<ApiResponse> {
    const response = await api.get(`/api/user/id/${userId}`);
    return response.data;
}

export async function updateIsPrivate(isPrivate: boolean): Promise<ApiResponse> {
    const response = await api.patch('/api/user/private', {
        is_private: isPrivate
    });
    return response;
}

// Helper function for privacy settings
export async function updatePrivacySetting<K extends keyof PrivacySettings>(
    category: string,
    key: string,
    value: PrivacySettings[K]
): Promise<ApiResponse> {
    try {
        const response = await api.patch('/api/user/privacy', {
            category,
            key,
            value
        });
        return response;
    } catch (error) {
        console.error('Error updating privacy setting:', error);
        throw error;
    }
}

// Helper function for preference settings
export async function updatePreferenceSetting<K extends keyof PreferenceSettings>(
    category: string,
    key: string,
    value: PreferenceSettings[K]
): Promise<ApiResponse> {
    try {
        const response = await api.patch('/api/user/preferences', {
            category,
            key,
            value
        });
        return response;
    } catch (error) {
        console.error('Error updating preference setting:', error);
        throw error;
    }
}

// Helper function for about settings
export async function updateAboutSetting<K extends keyof AboutSettings>(
    category: string,
    key: string,
    value: AboutSettings[K] extends AboutValue<infer T> ? T : never,
    privacy: PrivacyLevel = 'public'
): Promise<ApiResponse> {
    try {
        const response = await api.patch('/api/user/about', {
            category,
            key,
            value: {
                value,
                privacy
            }
        });
        return response;
    } catch (error) {
        console.error('Error updating about setting:', error);
        throw error;
    }
}

// Specific helper functions for common operations
export async function updatePrivateAccountStatus(isPrivate: boolean): Promise<ApiResponse> {
    return updateIsPrivate(isPrivate);
}

export async function updateDefaultAudience(audience: PrivacyLevel): Promise<ApiResponse> {
    return updatePrivacySetting('content', 'default_audience', audience);
}

export async function updateMessagePrivacy(recipients: PrivacySettings['message_receive']): Promise<ApiResponse> {
    return updatePrivacySetting('message', 'receive', recipients);
}

export async function updateOtpLogin(enabled: boolean): Promise<ApiResponse> {
    return updatePreferenceSetting('auth', 'otp_login', enabled);
}

export async function updateUserBio(bio: string, privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'brief', bio, privacy);
}

export async function updateUserLinks(links: Array<{ type: 'custom' | 'instagram' | 'x' | 'facebook' | 'youtube' | 'tiktok' | 'reddit', label: string; url: string }>, privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'links', links, privacy);
}

export async function updateTopIssue(issue: number | null, privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'issue', issue, privacy);
}

export async function updateAlignment(alignment: Record<string, string>, privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('alignment', 'values', alignment, privacy);
}

// New helper functions for user background information

/**
 * Updates the user's birthdate information
 * @param birthdate - ISO format date string (YYYY-MM-DD)
 * @param privacy - Privacy level for this information
 * @returns API response object
 */
export async function updateBioBirthdate(birthdate: string, privacy: PrivacyLevel = 'me'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'birthdate', birthdate, privacy);
}

/**
 * Updates the user's education information
 * @param education - Education information as string
 * @param privacy - Privacy level for this information
 * @returns API response object
 */
export async function updateBioEducation(education: string, privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'education', education, privacy);
}

/**
 * Updates the user's gender information
 * @param gender - Gender information as string
 * @param privacy - Privacy level for this information
 * @returns API response object
 */
export async function updateBioGender(gender: string, privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'gender', gender, privacy);
}

/**
 * Updates the user's age information
 * @param age - Age information as string
 * @param privacy - Privacy level for this information
 * @returns API response object
 */
export async function updateBioAge(age: number, privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'age', age, privacy);
}

/**
 * Updates the user's location information
 * @param location - Location information as string
 * @param privacy - Privacy level for this information
 * @returns API response object
 */
export async function updateBioLocation(location: string, privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'location', location, privacy);
}

/**
 * Updates the user's religious affiliation information
 * @param religions - Array of religious affiliations as strings
 * @param privacy - Privacy level for this information
 * @returns API response object
 */
export async function updateBioReligion(religion: string[], privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'religion', religion, privacy);
}

/**
 * Updates the user's political identities
 * @param identities - Array of political identity strings
 * @param privacy - Privacy level for this information
 * @returns API response object
 */
export async function updateBioPoliticalIdentities(identities: string[], privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'political_identities', identities, privacy);
}

/**
 * Updates the user's political party affiliations
 * @param parties - Array of political party strings
 * @param privacy - Privacy level for this information
 * @returns API response object
 */
export async function updateBioPoliticalParties(parties: string[], privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'political_parties', parties, privacy);
} 